import { ACTION_NAMES } from '@/constants/actionNames';
import type { AxiosResponse } from 'axios';
import type {
	FormAnswer,
	FormSubmissionStatus,
	RiskTier,
} from '@/components/Dashboard/Views/Settings/CfmSettings/SupportedChecks/Form/types';
import Repository from './repository';

const resource = '/cfm/form-submissions';

interface FindFormSubmissionsPayload {
	search?: string;
	dateFrom?: string;
	dateTo?: string;
	riskTiers?: RiskTier[];
	name?: string;
	entityId?: string;
	page: number;
	limit: number;
	orderBy?: string;
	orderByDesc?: boolean;
}

interface CreateFormSubmissionPayload {
	formId: string;
	answers: FormAnswer[];
	status: FormSubmissionStatus;
	submissionName?: string;
	caseId?: string;
	entityLinkId?: string;
}

interface UpdateFormSubmissionPayload {
	answers: FormAnswer[];
	status: FormSubmissionStatus;
	submissionName?: string;
}

interface OverrideScorePayload {
	finalScore: number;
	riskTier: RiskTier;
	reason: string;
}

export default {
	find(payload: FindFormSubmissionsPayload): Promise<AxiosResponse> {
		return Repository.postWithAuth(
			`${resource}/find`,
			payload,
			{},
			{ noCache: true }
		);
	},

	getById(id: string): Promise<AxiosResponse> {
		return Repository.getWithAuth(`${resource}/${id}`);
	},

	getEntityFieldSet(
		caseId: string,
		entityType: string
	): Promise<AxiosResponse> {
		const params = new URLSearchParams({ caseId, entityType });
		return Repository.getWithAuth(`/cfm/entity-field-set?${params.toString()}`);
	},

	create(payload: CreateFormSubmissionPayload): Promise<AxiosResponse> {
		const headers =
			payload.status === 'submitted'
				? { 'x-action-name': ACTION_NAMES.SUBMIT_FORM }
				: {};
		return Repository.postWithAuth(`${resource}`, payload, headers, {
			noCache: true,
		});
	},

	update(
		id: string,
		payload: UpdateFormSubmissionPayload
	): Promise<AxiosResponse> {
		const headers =
			payload.status === 'submitted'
				? { 'x-action-name': ACTION_NAMES.SUBMIT_FORM }
				: {};
		return Repository.putWithAuth(`${resource}/${id}`, payload, headers);
	},

	overrideScore(
		id: string,
		payload: OverrideScorePayload
	): Promise<AxiosResponse> {
		return Repository.postWithAuth(
			`${resource}/${id}/override-score`,
			payload,
			{ 'x-action-name': ACTION_NAMES.OVERRIDE_FORM_SCORE },
			{ noCache: true }
		);
	},

	deleteDraft(id: string): Promise<AxiosResponse> {
		return Repository.deleteWithAuth(`${resource}/${id}/draft`);
	},

	delete(id: string): Promise<AxiosResponse> {
		return Repository.deleteWithAuth(`${resource}/${id}`);
	},

	generatePDF(id: string): Promise<AxiosResponse> {
		return Repository.postWithAuth(
			`${resource}/${id}/pdf`,
			{},
			{},
			{ responseType: 'blob' }
		);
	},
};
